import { TextAreaField } from '@aws-amplify/ui-react'
import { DownloadForOfflineOutlined, UploadFileOutlined } from '@mui/icons-material'
import type { SelectChangeEvent } from '@mui/material'
import {
  Alert,
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AxiosError } from 'axios'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import PageHeader from '@/components/layouts/PageHeader'
import { TODAY } from '@/constants/dateTime'
import { useAlertContext } from '@/contexts/AlertContext'
import { useActivationGroupsQuery } from '@/features/activationGroup/hooks/useActivationGroupsQuery'
import BiddingWindowsPanel from '@/features/bidding/components/BiddingWindowsPanel'
import { LastEnergyBidTsvDialog } from '@/features/bidding/components/LastEnergyBidTsvDialog'
import SubmitButton from '@/features/bidding/components/SubmitButton'
import { BidType, MarketProgram } from '@/features/bidding/constants'
import {
  useBidExportsIntradayQuery,
  useIsFetchingBidExportsIntraday,
} from '@/features/bidding/hooks/useBidExportsIntradayQuery'
import {
  useImportIntradayEnergyOfferMutation,
  useIsImportingIntradayEnergyOffer,
} from '@/features/bidding/hooks/useImportIntradayEnergyOfferMutation'
import {
  isCapacityTsv,
  isCapacityTsvWindowClosed,
  resolveCapacityTsvMarket,
} from '@/features/bidding/utils/importOfferBiddingWindow'
import { useFeatureToggle } from '@/features/featureToggle/contexts/FeatureToggleContext'

const NOW_REFRESH_INTERVAL_MS = 30_000

function ImportIntradayEnergyOffer() {
  const { t, i18n } = useTranslation()
  const { pushAlert } = useAlertContext()
  const { isEnabled } = useFeatureToggle()
  // Gates only the capacity UI affordances (bid type toggle, capacity windows, market resolution).
  // The import path itself is one ungated endpoint either way — the backend decides what it accepts.
  const isCapacityImportEnabled = isEnabled('FLEX_PORTAL_TSV_CAPACITY_IMPORT')
  const isImportingEnergyOffer = useIsImportingIntradayEnergyOffer()
  const [tsvRows, setTsvRows] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const { importIntradayEnergyOffer } = useImportIntradayEnergyOfferMutation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedMarketProgram, setSelectedMarketProgram] = useState<MarketProgram>(MarketProgram.MFRR_UP)
  const isFetchingBidExports = useIsFetchingBidExportsIntraday()
  const [selectedDate, setSelectedDate] = useState<DateTime>(TODAY)
  const [selectedBidType, setSelectedBidType] = useState<BidType>(BidType.ENERGY)
  const bidType = isCapacityImportEnabled ? selectedBidType : BidType.ENERGY

  const [now, setNow] = useState<DateTime>(() => DateTime.now())

  const deliveryDay = selectedDate.toISODate() ?? TODAY.toISODate()!

  useEffect(() => {
    const interval = setInterval(() => setNow(DateTime.now()), NOW_REFRESH_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  // Advisory only: resolves the pasted TSV's capacity market via its activation groups (the same
  // signal the backend buckets on) to pick the right gate for the warnings. Any failure — query
  // error, unknown uuids, mixed markets — falls back to the FCR gate and never blocks the import.
  const { activationGroups } = useActivationGroupsQuery({ countryCode: 'fi' }, { enabled: isCapacityImportEnabled })
  const resolvedCapacityMarket = useMemo(() => {
    const marketProgramsByAgUuid = new Map((activationGroups ?? []).map((group) => [group.uuid, group.marketProgram]))
    return resolveCapacityTsvMarket(tsvRows, marketProgramsByAgUuid)
  }, [tsvRows, activationGroups])

  const marketProgramLabels: Record<string, string> = {
    [MarketProgram.MFRR_UP]: t('bidding.import_energy_offer.retrieve_last_bid.mfrr_up'),
    [MarketProgram.MFRR_DOWN]: t('bidding.import_energy_offer.retrieve_last_bid.mfrr_down'),
  }

  const { bidExportsIntraday, refetch } = useBidExportsIntradayQuery({
    portfolioCode: 'FI-A',
    marketProgram: selectedMarketProgram,
    deliveryDay,
    bidType: BidType.ENERGY,
  })

  const onRetrieveLastBid = async () => {
    const result = await refetch()
    if (result.isSuccess && result.data) {
      setIsDialogOpen(true)
    } else if (result.isError) {
      const error = result.error
      if (error instanceof AxiosError && error.response?.status === 404) {
        pushAlert({
          message: t('bidding.import_energy_offer.retrieve_last_bid.not_found', {
            date: deliveryDay,
            marketProgram: marketProgramLabels[selectedMarketProgram],
          }),
          severity: 'warning',
        })
      } else {
        pushAlert({
          message: t('bidding.import_energy_offer.retrieve_last_bid.fetch_failed'),
          severity: 'error',
        })
      }
    }
  }

  const onSubmit = async () => {
    await importIntradayEnergyOffer(tsvRows)
      .then(() => {
        setTsvRows('')
        setErrorMessage('')
        pushAlert({
          message: t('bidding.import_energy_offer.sending_successful.text'),
          severity: 'info',
          title: t('bidding.import_energy_offer.sending_successful.title'),
        })
      })
      .catch((error) => {
        if (error instanceof AxiosError && error.response?.data !== undefined) {
          const data = error.response.data
          let message: string
          if (typeof data === 'string') {
            message = data
          } else if (
            typeof data === 'object' &&
            data !== null &&
            'message' in data &&
            typeof data.message === 'string'
          ) {
            // Handle ApiError format: { name: string, message: string }
            message = data.message
          } else {
            message = JSON.stringify(data)
          }
          setErrorMessage(message)
        } else {
          setErrorMessage(error.message ?? t('bidding.import_energy_offer.sending_failed.unknown_error'))
        }
      })
  }

  // Advisory only, never blocks: with capacity import enabled warn when the pasted capacity TSV's
  // window has closed; with it disabled warn that capacity is not supported yet.
  function confirmDialogWarning(): string | undefined {
    if (!isCapacityImportEnabled) {
      return isCapacityTsv(tsvRows) ? t('bidding.import_energy_offer.capacity_unsupported_warning') : undefined
    }
    return isCapacityTsvWindowClosed(tsvRows, now, resolvedCapacityMarket?.gate)
      ? t('bidding.import_energy_offer.closing_warning.dialog')
      : undefined
  }

  const dialogProps = {
    title: t('bidding.import_energy_offer.dialog.title'),
    description: t('bidding.import_energy_offer.dialog.description'),
    warning: confirmDialogWarning(),
  }

  return (
    <>
      <PageHeader
        breadcrumbItems={[{ text: t('component.page_header.bidding'), to: '/bidding' }]}
        pageTitle={t('component.page_header.import_energy_offer')}
      />

      <Stack direction="column" mt={2} spacing={2}>
        <Alert severity="warning">{t('bidding.import_energy_offer.notice')}</Alert>

        {isCapacityImportEnabled && (
          <Box>
            <Typography gutterBottom color="text.secondary" display="block" variant="caption">
              {t('bidding.import_energy_offer.bid_type.label')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              data-testid="bid-type-toggle"
              size="small"
              value={selectedBidType}
              onChange={(_, newBidType: BidType | null) => {
                if (newBidType !== null) {
                  setSelectedBidType(newBidType)
                }
              }}
            >
              <ToggleButton value={BidType.ENERGY}>{t('bidding.import_energy_offer.bid_type.energy')}</ToggleButton>
              <ToggleButton value={BidType.CAPACITY}>{t('bidding.import_energy_offer.bid_type.capacity')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <BiddingWindowsPanel bidType={bidType} highlightedMarket={resolvedCapacityMarket?.family} now={now} />

        {bidType === BidType.ENERGY && (
          <>
            <Typography variant="h5">{t('bidding.import_energy_offer.retrieve_last_bid_section_title')}</Typography>
            <Stack alignItems="center" direction="row" spacing={2}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t('bidding.import_energy_offer.retrieve_last_bid.market_program_label')}</InputLabel>
                <Select
                  data-testid="market-program-selector"
                  label={t('bidding.import_energy_offer.retrieve_last_bid.market_program_label')}
                  value={selectedMarketProgram}
                  onChange={(event: SelectChangeEvent) => {
                    setSelectedMarketProgram(event.target.value as MarketProgram)
                  }}
                >
                  <MenuItem value={MarketProgram.MFRR_UP}>{marketProgramLabels[MarketProgram.MFRR_UP]}</MenuItem>
                  <MenuItem value={MarketProgram.MFRR_DOWN}>{marketProgramLabels[MarketProgram.MFRR_DOWN]}</MenuItem>
                </Select>
              </FormControl>
              <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
                <DatePicker
                  data-testid="delivery-day-picker"
                  enableAccessibleFieldDOMStructure={false}
                  format="LLL dd, yyyy"
                  label={t('bidding.import_energy_offer.retrieve_last_bid.delivery_day_label')}
                  slotProps={{ textField: { size: 'small' } }}
                  sx={{ width: 200 }}
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue ?? TODAY)}
                />
              </LocalizationProvider>
              <CustomButton
                data-testid="retrieve-last-bid-button"
                disabled={isFetchingBidExports}
                startIcon={<DownloadForOfflineOutlined />}
                variant="outlined"
                onClick={onRetrieveLastBid}
              >
                {t('bidding.import_energy_offer.retrieve_last_bid.button', { date: deliveryDay })}
              </CustomButton>
            </Stack>
          </>
        )}

        <Divider />

        <Typography variant="h5">{t('bidding.import_energy_offer.import_section_title')}</Typography>

        {errorMessage !== '' && (
          <Alert data-testid="server-error" severity="error">
            <p>{t('bidding.import_energy_offer.sending_failed.error_message_from_server')}</p>
            {/* Preserve whitespace in monospace: TSV errors often differ only in invisible characters */}
            <p style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{errorMessage}</p>
          </Alert>
        )}

        <TextAreaField
          data-testid="tsv-rows"
          label={t('bidding.import_energy_offer.tsv')}
          placeholder={t('bidding.import_energy_offer.tsv_placeholder')}
          resize="vertical"
          rows={10}
          value={tsvRows}
          onChange={(e) => setTsvRows(e.target.value)}
        />

        <Box data-testid="submit-button">
          <SubmitButton
            buttonProps={{
              startIcon: <UploadFileOutlined />,
              disabled: isImportingEnergyOffer || tsvRows === '',
            }}
            dialogProps={dialogProps}
            shouldShowConfirmation={true}
            onSubmit={onSubmit}
          >
            {t('bidding.import_energy_offer.submit')}
          </SubmitButton>
        </Box>
      </Stack>

      <LastEnergyBidTsvDialog
        deliveryDay={deliveryDay}
        marketProgram={marketProgramLabels[selectedMarketProgram]}
        open={isDialogOpen}
        tsvContent={bidExportsIntraday ?? ''}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  )
}

export default ImportIntradayEnergyOffer
